/**
 * Scanned barcode normalisation.
 *
 * **Why this exists.** Labels printed before this application took over label
 * printing carry a `--` prefix in their barcode: the legacy DYMO path encoded
 * `--<contract>` (`old/delphi/hlOLE_Delphi_main_for_me.pas:403`). Those labels are
 * physically attached to pledged objects sitting on shop shelves and will keep
 * arriving at a counter for as long as the contracts run. New labels — Zebra and
 * DYMO alike — encode the canonical payload with no prefix.
 *
 * So a scanner at a counter can produce either form, and both must find the same
 * contract. This module is the one place that reconciles them.
 *
 * **This is backward compatibility for physical labels already in circulation.** It
 * is not compatibility with the legacy Delphi application: nothing here helps that
 * application read anything, and new labels deliberately do not carry the sentinel.
 * When the last pre-migration label has left the shelves, this step can be deleted
 * and nothing else has to change.
 *
 * **Scope, deliberately narrow.** Exactly one leading sentinel is removed, and only
 * when it is there. Everything else is passed through untouched, so the existing
 * validation and lookup run on both forms unchanged — a value that was malformed
 * before normalisation is still malformed after it, and is rejected by the same
 * code that already rejected it.
 *
 * Pure: no I/O, no state.
 */

/**
 * Prefix the legacy DYMO labels carry.
 *
 * Two hyphens, chosen by the legacy because a scanner types its payload into
 * whatever field has focus and the prefix let the old application tell a scan from
 * something typed by hand.
 */
export const LEGACY_BARCODE_SENTINEL = '--';

/**
 * Strip the whitespace a scanner adds around the payload.
 *
 * Barcode scanners in keyboard-wedge mode append a terminator — usually CR, often
 * CRLF, occasionally a tab when configured for form navigation. It is not part of
 * the encoded data and never was.
 *
 * **Why this is not a regex.** This used to be `/(?:^\s+)|(?:\s+$)/g`, which SonarQube
 * flagged as `typescript:S5852` — super-linear runtime through backtracking. The
 * `\s+$` branch is the problem: `$` is only reachable at the very end, so for input
 * that is mostly whitespace but does *not* end in whitespace, the engine starts
 * `\s+` at each of the N positions, consumes greedily to the end, fails `$`, and
 * backtracks character by character. That is O(N²). `'\t'.repeat(100_000) + 'x'`
 * takes seconds; the same string an order of magnitude longer hangs the tab.
 *
 * A scanner is not a trustworthy length bound. In keyboard-wedge mode it types into
 * whatever field has focus, the same field a person can paste into, and a
 * misconfigured or hostile scanner can emit an arbitrarily long run.
 *
 * `String.prototype.trim()` removes exactly the same character set — ECMAScript
 * defines it over WhiteSpace + LineTerminator, which is precisely what `\s` matches,
 * U+FEFF (BOM) and U+00A0 (NBSP) included - in a single linear pass with no
 * this is a behaviour-preserving substitution, not a narrowing one, and it removes
 * the vulnerable construct rather than suppressing the report.
 */
function stripSurroundingWhitespace(value: string): string {
    return value.trim();
}

/**
 * Reduce a scanned value to the payload the application's lookups expect.
 *
 * Two steps, in order:
 *
 * 1. Strip surrounding whitespace, including the CR/LF a keyboard-wedge scanner
 *    appends.
 * 2. Remove **exactly one** leading {@link LEGACY_BARCODE_SENTINEL}, and only if the
 *    value begins with it.
 *
 * Step 2 is anchored and single on purpose. Removing the first two characters
 * unconditionally would silently corrupt a canonical payload — `12345678` would
 * become `345678` and resolve to a different contract, which is precisely the kind
 * of error nobody would notice until a customer queried it. A global replacement
 * would eat a `--` occurring legitimately inside a payload.
 *
 * The result is **not** validated here. It is handed to whichever validation and
 * lookup the call site already ran, so both barcode formats take the identical path
 * from this point on.
 *
 * @param value Raw text as the scanner or the operator produced it.
 * @returns The normalised payload; an empty string for empty or absent input.
 */
export function normalizeScannedBarcode(value: string | null | undefined): string {
    if (typeof value !== 'string') {
        return '';
    }

    const trimmed = stripSurroundingWhitespace(value);

    if (!trimmed.startsWith(LEGACY_BARCODE_SENTINEL)) {
        return trimmed;
    }

    // slice, not a regex: `substring(2)` would not carry the "only when present"
    // condition that makes this safe.
    return stripSurroundingWhitespace(trimmed.slice(LEGACY_BARCODE_SENTINEL.length));
}

/**
 * Whether a scanned value carries the legacy prefix.
 *
 * Exposed for diagnostics and for messages that want to distinguish an old label
 * from a new one. Lookups should call {@link normalizeScannedBarcode} rather than
 * branching on this.
 */
export function hasLegacyBarcodeSentinel(value: string | null | undefined): boolean {
    return (
        typeof value === 'string' &&
        stripSurroundingWhitespace(value).startsWith(LEGACY_BARCODE_SENTINEL)
    );
}
